import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Icon, Label, Loader, Message, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    getCompanyBillingThunk,
    selectBillingState,
    startExtensionCheckoutThunk,
} from '../../slices/billingSlice';
import { activateTrialThunk, cancelCompanySubscriptionThunk, selectCompanies } from '../../slices/companiesSlice';
import { selectUser } from '../../slices/userSlice';
import { describeDuration, describeRemaining, formatDate, formatMoney } from '../../utils/Billing';
import { getParams } from '../../utils/System';
import CommonModal from '../modal/Modal';

/*
* The billing payload sends the stored status code, not the display label the
* company list carries, so these are the codes rather than SubscriptionStatus.
*/
const STATUS_TRIAL = 'TR';
const STATUS_NONE = 'NO';

/*
* A company's own billing page.
*
* The offer is a free trial, then one payment that buys a fixed block of extra
* time, then a conversation with us. None of those numbers are written down
* here - a company can be quoted its own price, so everything comes from the
* billing payload the API sends.
*/
/*
* Rendered both on its own route and folded into an accordion on the company
* page, which already has a heading of its own - hence showHeading.
*/
interface ICompanyAdminProps {
    showHeading?: boolean;
}

const CompanyAdmin = ({ showHeading = true }: ICompanyAdminProps) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const companyId = parseInt(params?.companyId!);
    const company = useAppSelector(selectCompanies).find(c => c.id === companyId);
    const { data: billing, status, checkoutStatus, error } = useAppSelector(selectBillingState);
    const user = useAppSelector(selectUser);
    const isCompanyAdmin = user?.companies?.find(c => c.id === companyId)?.isAdmin === true;
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    // Stripe sends the customer back here with ?billing=success. The payment is
    // confirmed by webhook, not by this redirect, so the banner says "on its
    // way" and the re-fetch below is what actually reports the new end date.
    const checkoutOutcome = getParams().get('billing');

    useEffect(() => {
        if (companyId) {
            dispatch(getCompanyBillingThunk(companyId));
        }
    }, [companyId]);

    if (!company) {
        console.error(`Cannot find company with id ${companyId}`);
        return null;
    }

    if (!billing) {
        return status === 'failed'
            ? <Message negative header='Could not load billing' content={error?.reason} />
            : <Loader active inline='centered' />;
    }

    const onTrial = billing.status === STATUS_TRIAL;
    const extensionLength = describeDuration(billing.extensionDays);
    const extensionPrice = formatMoney(billing.extensionPriceCents, billing.currency);

    return (
        <div id='company-admin'>
            {isCancelModalOpen &&
                <CommonModal
                    header='Cancel this account?'
                    content='Your team loses access to Sub Ratings. This cannot be reversed.'
                    onContinue={() => dispatch(cancelCompanySubscriptionThunk(company.id))}
                    onClose={() => setIsCancelModalOpen(false)}
                />
            }

            {showHeading && <h2>Subscription</h2>}

            {checkoutOutcome === 'success' &&
                <Message
                    positive
                    icon='check circle'
                    header='Thanks - your payment is on its way'
                    content={`Your ${extensionLength} extension is applied as soon as Stripe confirms the payment, usually within a few seconds. Refresh this page to see the new end date.`}
                />
            }
            {checkoutOutcome === 'canceled' &&
                <Message
                    info
                    header='Checkout cancelled'
                    content='Nothing was charged. You can start again whenever you are ready.'
                />
            }
            {error && checkoutStatus === 'failed' &&
                <Message negative header='Could not start checkout' content={error.reason} />
            }

            {billing.isPastDue &&
                <Message
                    warning
                    icon='clock'
                    header='Your annual invoice is outstanding'
                    content={
                        `We emailed your renewal invoice and it has not been paid yet. Your access `
                        + `continues until ${formatDate(billing.graceEndsOn)}. If the invoice went to the `
                        + `wrong person or you need another copy, email ${billing.contactEmail}.`
                    }
                />
            }

            <StyledStatus>
                <StyledStatusLine>
                    <strong>Status:</strong> {billing.statusLabel}
                    {billing.hasLapsed &&
                        <Label color='red' size='small' horizontal>Expired</Label>
                    }
                    {billing.hasAnnualPlan &&
                        <Label color='green' size='small' horizontal>Annual plan</Label>
                    }
                    {billing.isCustomPlan && !billing.hasAnnualPlan &&
                        <Label color='blue' size='small' horizontal>Custom pricing</Label>
                    }
                </StyledStatusLine>

                {/*
                  * An annual customer's headline is the renewal, not a trial
                  * countdown - they are past that stage.
                  */}
                {billing.hasAnnualPlan &&
                    <>
                        <StyledStatusLine>
                            <strong>Annual plan:</strong> {billing.annualPriceDisplay} per year
                        </StyledStatusLine>
                        {billing.annualRenewalDate &&
                            <StyledStatusLine>
                                <strong>Next invoice:</strong> {formatDate(billing.annualRenewalDate)}
                                {' '}(emailed, payable within {billing.invoiceDaysUntilDue} days)
                            </StyledStatusLine>
                        }
                    </>
                }

                {!billing.hasAnnualPlan && onTrial && billing.trialExpiration &&
                    <StyledStatusLine>
                        <strong>Access through:</strong> {formatDate(billing.trialExpiration)}
                        {' '}({describeRemaining(billing.daysRemaining)})
                    </StyledStatusLine>
                }

                {billing.eligibleForTrial &&
                    <StyledStatusLine>
                        Start your {describeDuration(billing.freeTrialDays)} free trial - no credit card required.
                    </StyledStatusLine>
                }
            </StyledStatus>

            {billing.eligibleForTrial &&
                <Button positive onClick={() => dispatch(activateTrialThunk(company.id))}>
                    Start free trial
                </Button>
            }

            {/*
              * The paid step. Shown only to a company admin, because they are
              * the ones who can actually authorise a payment.
              */}
            {billing.canPurchaseExtension && isCompanyAdmin &&
                <StyledOffer>
                    <h3>Keep going for another {extensionLength}</h3>
                    <p>
                        {billing.hasLapsed
                            ? `Your trial has ended. ${extensionPrice} restores access for ${extensionLength}.`
                            : `${extensionPrice} extends your access by ${extensionLength}, added to the time you have left.`}
                    </p>
                    <Button
                        primary
                        size='large'
                        loading={checkoutStatus === 'loading'}
                        disabled={checkoutStatus === 'loading'}
                        onClick={() => dispatch(startExtensionCheckoutThunk(company.id))}
                    >
                        Extend for {extensionPrice} <Icon className='arrow right' />
                    </Button>
                    <StyledFinePrint>
                        A one-time payment, not a subscription. Nothing renews automatically.
                    </StyledFinePrint>
                </StyledOffer>
            }

            {billing.canPurchaseExtension && !isCompanyAdmin &&
                <Message
                    info
                    header={`${extensionPrice} extends your access by ${extensionLength}`}
                    content='An admin on your Procore company account can purchase this from their own Subscription page.'
                />
            }

            {/* Past the trial, pricing is a conversation rather than a button. */}
            <StyledContact>
                <h3>{billing.hasAnnualPlan ? 'Questions about your plan?' : 'Looking further ahead?'}</h3>
                <p>
                    {billing.hasAnnualPlan
                        ? `Changes to your plan, invoicing or billing contacts - or adding another Procore company - are all a quick email away.`
                        : `For pricing beyond the trial - annual terms, multiple Procore companies, or anything else your team needs - get in touch and we will put together a quote.`}
                </p>
                <Button as='a' href={`mailto:${billing.contactEmail}?subject=Sub Ratings pricing for ${encodeURIComponent(company.name)}`}>
                    <Icon name='mail' /> {billing.hasAnnualPlan ? 'Contact us' : 'Contact us about pricing'}
                </Button>
            </StyledContact>

            {isCompanyAdmin && billing.events.length > 0 &&
                <StyledHistory>
                    <h3>Billing history</h3>
                    <Table basic='very' compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Date</Table.HeaderCell>
                                <Table.HeaderCell>What happened</Table.HeaderCell>
                                <Table.HeaderCell>Time added</Table.HeaderCell>
                                <Table.HeaderCell textAlign='right'>Amount</Table.HeaderCell>
                                <Table.HeaderCell>Invoice / access through</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {billing.events.map(event => (
                                <Table.Row key={event.id}>
                                    <Table.Cell>{formatDate(event.completedAt || event.createdAt)}</Table.Cell>
                                    <Table.Cell>{event.kindLabel}</Table.Cell>
                                    <Table.Cell>{event.days ? describeDuration(event.days) : '-'}</Table.Cell>
                                    <Table.Cell textAlign='right'>
                                        {event.amountCents ? formatMoney(event.amountCents, event.currency) : '-'}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {event.invoiceUrl
                                            ? <a href={event.invoiceUrl} target='_blank' rel='noreferrer'>
                                                {event.invoiceNumber || 'View invoice'}
                                            </a>
                                            : formatDate(event.newExpiration)}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </StyledHistory>
            }

            {isCompanyAdmin && billing.status !== STATUS_NONE && !billing.hasAnnualPlan &&
                <StyledCancel>
                    <Button basic size='small' onClick={() => setIsCancelModalOpen(true)}>
                        Cancel account
                    </Button>
                </StyledCancel>
            }
        </div>
    );
};

const StyledStatus = styled('div')`
    margin-bottom: 20px;
`;

const StyledStatusLine = styled('div')`
    line-height: 2;
    & .ui.label {
        margin-left: 8px;
    }
`;

const StyledOffer = styled('div')`
    background: aliceblue;
    border-radius: 6px;
    margin: 20px 0;
    padding: 20px;
    & h3 {
        margin-top: 0;
    }
`;

const StyledFinePrint = styled('p')`
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9em;
    margin: 12px 0 0;
`;

const StyledContact = styled('div')`
    border-top: 1px solid rgba(34, 36, 38, 0.15);
    margin-top: 30px;
    padding-top: 20px;
`;

const StyledHistory = styled('div')`
    margin-top: 30px;
`;

const StyledCancel = styled('div')`
    border-top: 1px solid rgba(34, 36, 38, 0.15);
    margin-top: 30px;
    padding-top: 20px;
`;

export default CompanyAdmin;
